import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Button, Card, CardSubtitle, CardTitle, Field, Input, Select } from '@keobook/ui';
import { api, ApiError } from '../lib/api';
import { Layout } from '../components/Layout';

type Account = Awaited<ReturnType<typeof api.getAccounts>>['accounts'][number];
// Migration 0006 added `cost_of_revenue` so the P&L can compute a real
// Gross Profit. Every kind the DB enum can hold must appear here, or
// the runtime `grouped[a.kind].push(a)` below trips on an undefined
// bucket and the page blank-screens with the React-DOM "Cannot read
// properties of undefined (reading 'push')" trace.
type Kind = 'asset' | 'liability' | 'equity' | 'revenue' | 'cost_of_revenue' | 'expense';

const KIND_LABEL: Record<Kind, string> = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  revenue: 'Revenue',
  cost_of_revenue: 'Cost of revenue',
  expense: 'Expense',
};

// Ordered for display — assets first, then the rest of the balance-
// sheet stack, then the income-statement order (Revenue → Cost of
// Revenue → Operating Expenses) so the on-screen chart mirrors how
// the P&L itself reads.
const KIND_ORDER: Kind[] = [
  'asset',
  'liability',
  'equity',
  'revenue',
  'cost_of_revenue',
  'expense',
];

export function Accounts() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<Kind>('expense');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.getAccounts();
      const sorted = [...r.accounts].sort((a, b) => a.code.localeCompare(b.code));
      setAccounts(sorted);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load the chart of accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.createAccount({
        code: newCode.trim(),
        name: newName.trim(),
        kind: newKind,
      });
      setNotice(`Added ${newCode} — ${newName}.`);
      setNewCode('');
      setNewName('');
      setAdding(false);
      await load();
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const p = err.problem as { title?: string };
        setError(p.title ?? `Add failed (${err.status}).`);
      } else {
        setError(err instanceof Error ? err.message : 'Add failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onRename(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.patchAccount(id, { name: editName.trim() });
      setEditing(null);
      setEditName('');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Rename failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onArchiveToggle(a: Account) {
    setBusy(true);
    setError(null);
    try {
      await api.patchAccount(a.id, { isArchived: !a.isArchived });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  }

  const grouped: Record<Kind, Account[]> = {
    asset: [],
    liability: [],
    equity: [],
    revenue: [],
    cost_of_revenue: [],
    expense: [],
  };
  for (const a of accounts) grouped[a.kind as Kind].push(a);

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: '0.5rem',
          }}
        >
          <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--kb-gray-900)', margin: 0 }}>
            Chart of accounts
          </h1>
          {!adding && (
            <Button size="sm" onClick={() => setAdding(true)}>
              + Add account
            </Button>
          )}
        </div>
        <p className="kb-muted" style={{ marginTop: 0, marginBottom: '1.5rem' }}>
          Rename and archive freely; codes and account kinds are immutable to keep the audit trail
          clean.
        </p>

        {error && (
          <div role="alert" className="kb-form-error">
            {error}
          </div>
        )}
        {notice && (
          <div className="kb-form-notice" role="status">
            {notice}
          </div>
        )}

        {adding && (
          <Card>
            <CardTitle>Add a new account</CardTitle>
            <form onSubmit={onAdd}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  marginTop: '0.75rem',
                }}
              >
                <Field label="Code" required>
                  <Input
                    value={newCode}
                    onChange={(e) => setNewCode(e.currentTarget.value)}
                    placeholder="6210"
                    required
                  />
                </Field>
                <Field label="Name" required>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.currentTarget.value)}
                    placeholder="Stripe processing fees"
                    required
                  />
                </Field>
                <Field label="Kind">
                  <Select
                    value={newKind}
                    onChange={(e) => setNewKind(e.currentTarget.value as Kind)}
                  >
                    {KIND_ORDER.map((k) => (
                      <option key={k} value={k}>
                        {KIND_LABEL[k]}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button type="submit" disabled={busy || !newCode.trim() || !newName.trim()}>
                  {busy ? 'Adding…' : 'Add account'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        <div
          style={{
            marginTop: adding ? '1.25rem' : 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {loading ? (
            <Card>
              <CardSubtitle>Loading…</CardSubtitle>
            </Card>
          ) : (
            KIND_ORDER.map((kind) => {
              const rows = grouped[kind];
              if (rows.length === 0) return null;
              return (
                <Card key={kind}>
                  <CardTitle>{KIND_LABEL[kind]}</CardTitle>
                  <table className="kb-report" style={{ marginTop: '0.5rem' }}>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Normal side</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((a) => (
                        <tr key={a.id} style={a.isArchived ? { opacity: 0.5 } : undefined}>
                          <td>{a.code}</td>
                          <td>
                            {editing === a.id ? (
                              <Input
                                value={editName}
                                onChange={(e) => setEditName(e.currentTarget.value)}
                                style={{ minWidth: 220 }}
                              />
                            ) : (
                              a.name
                            )}
                          </td>
                          <td className="kb-muted">{a.normalSide}</td>
                          <td className="kb-report__amt">
                            {editing === a.id ? (
                              <>
                                <button
                                  type="button"
                                  className="kb-linkbtn"
                                  disabled={busy}
                                  onClick={() => onRename(a.id)}
                                  style={{ marginRight: 10 }}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="kb-linkbtn"
                                  onClick={() => {
                                    setEditing(null);
                                    setEditName('');
                                  }}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="kb-linkbtn"
                                  onClick={() => {
                                    setEditing(a.id);
                                    setEditName(a.name);
                                  }}
                                  style={{ marginRight: 10 }}
                                >
                                  Rename
                                </button>
                                <button
                                  type="button"
                                  className="kb-linkbtn"
                                  onClick={() => onArchiveToggle(a)}
                                >
                                  {a.isArchived ? 'Unarchive' : 'Archive'}
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
}
